import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import { MeetingNotes as MeetingNotesType } from "../../types/meetings";
import { useState, useEffect } from "react";
import { ActionItemsSection } from "./ActionItemsSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useMediaQuery } from "../../hooks/use-mobile";
import { EditableField } from "../action-item/EditableField";
import { useQueryClient } from "@tanstack/react-query";
import { TranscriptSection } from "./sections/TranscriptSection";
import { EnhancedAnalysis } from "./sections/EnhancedAnalysis";
import { DetailedNotesSection } from "./sections/DetailedNotesSection";
import { KeyPointsSection } from "./sections/KeyPointsSection";
import { DecisionsSection } from "./sections/DecisionsSection";
import { useToast } from "../ui/use-toast";
import { meetingNotesService } from "../../services/supabase/meeting-notes";
import { parseJsonSafely } from "../../lib/formatting-utils";
import React from "react";

interface MeetingNotesProps {
  notes: MeetingNotesType;
  meetingId?: string;
  userId?: string;
  onActionItemChange: () => void;
  actionItems: any[];
}

const tabs = [
  { name: 'Overview', id: 'overview' },
  { name: 'Notes', id: 'notes' },
  { name: 'Action Items', id: 'action-items' },
  { name: 'Transcript', id: 'transcript' },
];

export function MeetingNotes({ notes, meetingId, userId, onActionItemChange, actionItems }: MeetingNotesProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const isMobile = useMediaQuery("(max-width: 768px)");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (isMobile) {
      setActiveTab('overview');
    } else if (activeTab === 'overview') {
      setActiveTab('notes');
    }
  }, [isMobile]);

  const handleFieldUpdate = async (field: keyof MeetingNotesType, value: string) => {
    if (!meetingId || !userId) return;

    try {
      await meetingNotesService.updateField(meetingId, userId, field, value);
      
      toast({
        title: "Success",
        description: "Meeting notes updated successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['meetingNotes', meetingId] });
      
    } catch (error) {
      console.error('Error in handleFieldUpdate:', error);
      toast({
        title: "Error",
        description: "Failed to update meeting notes",
        variant: "destructive",
      });
      throw error;
    }
  };

  const renderOverviewContent = () => {
    const parsedSentiment = parseJsonSafely(notes.sentiment);
    
    return (
      <div className="space-y-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <div className="md:col-span-2 h-full">
            <Card className="h-full">
              <CardContent className="h-full flex flex-col p-6">
                <h2 className="text-lg font-semibold mb-2">Summary</h2>
                <div className="flex-1 min-h-0">
                  <EditableField
                    value={notes.summary || ""}
                    onChange={(value) => handleFieldUpdate('summary', value)}
                    type="textarea"
                    placeholder="Add a summary..."
                    showToast={true}
                    className="h-full"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="h-full">
            <Card className="h-full">
              <CardContent className="h-full flex flex-col p-6">
                <h2 className="text-lg font-semibold mb-2">Sentiment</h2>
                <div className="flex-1 min-h-0">
                  <EditableField
                    value={parsedSentiment?.sentiment || ""}
                    onChange={(value) => handleFieldUpdate('sentiment', JSON.stringify({ sentiment: value }))}
                    type="textarea"
                    placeholder="Analyzing sentiment..."
                    showToast={true}
                    className="h-full"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {notes.metadata && Object.keys(notes.metadata).length > 0 && (
          <EnhancedAnalysis analysis={notes.metadata} sentiment={notes.sentiment} />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {!isMobile && renderOverviewContent()}

      {isMobile && (
        <Select
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select section" />
          </SelectTrigger>
          <SelectContent>
            {tabs.map((tab) => (
              <SelectItem key={tab.id} value={tab.id}>
                {tab.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {!isMobile && (
        <div className="border-b border-gray-100 pl-4 pr-4">
          <nav className="-mb-px flex space-x-8">
            {tabs.filter(tab => tab.id !== 'overview').map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "whitespace-nowrap border-b-2 text-sm font-medium",
                  activeTab === tab.id
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                )}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="mt-6">
        {(isMobile && activeTab === 'overview') && renderOverviewContent()}
        
        {activeTab === 'notes' && (
          <Card>
            <CardContent className="space-y-8 text-sm">
              <KeyPointsSection 
                keyPoints={notes.key_points || ""} 
                meetingId={meetingId}
                userId={userId}
              />
              <DetailedNotesSection 
                notes={notes.detailed_notes || ""} 
                meetingId={meetingId}
                userId={userId}
              />
              <DecisionsSection
                decisions={notes.decisions || null}
                meetingId={meetingId}
                userId={userId}
              />
            </CardContent>
          </Card>
        )} 
        
        {activeTab === 'action-items' && (
          <ActionItemsSection
            actionItems={actionItems}
            meetingId={meetingId}
            userId={userId}
            onActionItemChange={onActionItemChange}
          />
        )}

        {activeTab === 'transcript' && meetingId && (
          <Card>
            <CardContent className="pt-6">
              <TranscriptSection meetingId={meetingId} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
